
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { MessageCircle } from 'lucide-react';

interface PayToChatButtonProps {
  reviewId: string;
  reviewRef: string;
}

const PayToChatButton = ({ reviewId, reviewRef }: PayToChatButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !termsAgreed) {
      toast({
        title: "Error",
        description: "Please fill in your email and agree to the terms and conditions.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('review_connections')
        .insert({
          review_id: reviewId,
          requester_email: email,
          requester_user_id: user?.id || null,
          terms_agreed: termsAgreed,
          status: 'pending'
        });

      if (error) throw error;

      toast({
        title: "Request Submitted",
        description: "Your request has been submitted for admin review. You'll be notified via email once approved.",
      });
      
      setIsOpen(false);
      setEmail('');
      setTermsAgreed(false);
    } catch (error: any) {
      console.error("Error submitting request:", error);
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm"
          className="text-redflags-red border-redflags-red hover:bg-redflags-red/10 text-xs px-2 py-1 h-7"
        >
          <MessageCircle className="w-3 h-3 mr-1" />
          Request to chat
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Request to Chat with Review Author</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="email">Your Email Address</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
            />
            <p className="text-sm text-gray-600">
              We'll use this email to contact you once your request is approved.
            </p>
          </div>
          
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Review Reference: <span className="font-mono font-semibold">{reviewRef}</span>
            </p>
            
            <div className="flex items-start space-x-2">
              <Checkbox
                id="terms"
                checked={termsAgreed}
                onCheckedChange={(checked) => setTermsAgreed(checked as boolean)}
              />
              <Label htmlFor="terms" className="text-sm leading-5">
                I agree to the terms and conditions for requesting private contact with review authors
              </Label>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !email || !termsAgreed}
              className="bg-redflags-red hover:bg-redflags-red/90"
            >
              {isSubmitting ? "Submitting..." : "Submit Request"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PayToChatButton;
